'use client';

import { lazy, type ReactElement, Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBasketSnapshot } from '@/providers/basket';
import { useMiniCartDataLoader } from '@/hooks/use-mini-cart-data';
import { setMiniCartOpen, useMiniCartStore } from '@/hooks/mini-cart-store';
import { BagIcon, IconState } from '@/shared-ui';
import { cn } from '@/lib/utils';
import { USER_ACTION_ITEM_CLASSES } from './item-classes';

const Minibag = lazy(() => import('@/features/minibag').then((module) => ({ default: module.Minibag })));

/**
 * Cart badge: the bag icon with a live item-count bubble. Lazy-mounts the minibag drawer on first open —
 * a click here, or an external open (add-to-cart flips `miniCartOpen`).
 */
export const CartBadge = ({ className }: { className?: string }): ReactElement => {
    const snapshot = useBasketSnapshot();
    const { t } = useTranslation('cart');
    const numberOfItems = snapshot?.totalItemCount ?? 0;
    const hasItems = numberOfItems > 0;
    const [clicked, setClicked] = useState<boolean>(false);
    const miniCartOpen = useMiniCartStore((s) => s.open);

    // Warm the mini-cart data on hover/focus so it's ready by click (loader dedups). Skip when empty —
    // nothing to enrich, and it avoids a network call on a low-engagement hover.
    const loadMiniCartData = useMiniCartDataLoader();
    const prefetch = useCallback(() => {
        if (!snapshot?.basketId || !hasItems) {
            return;
        }
        loadMiniCartData();
    }, [snapshot?.basketId, hasItems, loadMiniCartData]);

    // Lazy-mount the minibag when opened externally (e.g. add-to-cart).
    if (miniCartOpen && !clicked) {
        setClicked(true);
    }

    return (
        <>
            <button
                type="button"
                className={cn(USER_ACTION_ITEM_CLASSES, className)}
                onClick={() => {
                    setClicked(true);
                    setMiniCartOpen(true);
                }}
                onPointerEnter={prefetch}
                onFocus={prefetch}
                aria-label={t('badge.ariaLabel', { count: numberOfItems })}>
                {/* Always shown (incl. "0") per design. */}
                <IconState content={numberOfItems} color="bg-snk-cart-green" indicatorClassName="!h-3/5 !w-3/5">
                    <BagIcon className="size-6" />
                </IconState>
            </button>
            {clicked && (
                <Suspense fallback={null}>
                    <Minibag />
                </Suspense>
            )}
        </>
    );
};
